// Config Types
export interface Config {
  logo: string;
  url: string;
  protocol: string;
  isVerified: boolean;
  pageTitle: string;
  primaryColor: string;
  dataExported: string;
}

// Footer Types
export interface FooterButton {
  title: string;
  desc?: string | null;
  icon?: string | null;
  link?: string;
  href?: string; // Support both link and href
  color?: string; // Optional color property
}

export interface FooterData {
  sectionId: number;
  backgroundColor: string;
  title: string;
  buttons: FooterButton[];
}

export interface Footer {
  address: string;
  phone: string;
  email: string;
  copyright: string;
  by: string;
  data: FooterData;
}

// Section Data Types
export interface BannerData {
  banner: string;
  title: string | null;
  url: string | null;
}

export interface RegularData {
  title: string;
  desc: string | null;
  icon: string | null;
  isHeader: boolean;
  category: string | null;
  link: string | null;
  btnTitle: string | null;
  buttinTitle: string | null;
}

export interface Section5Data {
  title: string;
  buttinTitle: string;
  link: string;
}

export interface Section6Data {
  title: string;
  desc: string;
  icon: string;
}

export interface Section7Data {
  title: string;
  desc: string;
  icon: string;
}

export interface Section8Data {
  image: string;
  icon: string;
}

export interface Section9Data {
  title: string;
  desc: string;
  embed: string;
}

// Union type for all section data types
export type SectionData =
  | BannerData
  | RegularData
  | Section5Data
  | Section6Data
  | Section7Data
  | Section8Data
  | Section9Data;

// Section Types
export interface BaseSection {
  sectionId: number;
  backgroundColor: string;
}

export interface SectionWithDatas extends BaseSection {
  datas: SectionData[];
  data?: never;
}

export interface SectionWithData extends BaseSection {
  data: Section9Data;
  datas?: never;
}

export type Section = SectionWithDatas | SectionWithData;

// Page Types
export interface Page {
  page: string;
  href: string;
  pages: Page[];
  title: string;
  desc: string;
  sections: Section[];
  icon?: string; // Optional icon property
}

// FAQ Types
export interface FAQ {
  title: string;
  description: string;
}

// Advice Types
export interface Advice {
  id: number;
  title: string;
  image: string | null;
  videoUrl: string | null;
  desc: string | null;
}

// Main Data Type
export interface AppData {
  config: Config;
  footer: Footer;
  datas: Page[];
  faqs: FAQ[];
  advices?: Advice[];
}

// Type Guards
export function isBannerData(data: SectionData): data is BannerData {
  return "banner" in data;
}

export function isRegularData(data: SectionData): data is RegularData {
  return "isHeader" in data || ("category" in data && "link" in data);
}

export function isSection5Data(data: SectionData): data is Section5Data {
  console.log('data: ', data)
  return "buttinTitle" in data && "link" in data && !("isHeader" in data);
}

export function isSection6Data(data: SectionData): data is Section6Data {
  return (
    "icon" in data &&
    "desc" in data &&
    typeof data.desc === "string" &&
    !("isHeader" in data) &&
    !("banner" in data)
  );
}

export function isSection7Data(data: SectionData): data is Section7Data {
  return (
    "icon" in data &&
    "desc" in data &&
    typeof data.desc === "string" &&
    !("isHeader" in data) &&
    !("banner" in data) &&
    !("image" in data)
  );
}

export function isSection8Data(data: SectionData): data is Section8Data {
  return "image" in data && "icon" in data;
}

export function isSectionWithDatas(
  section: Section
): section is SectionWithDatas {
  return "datas" in section && section.datas !== undefined;
}

export function isSectionWithData(
  section: Section
): section is SectionWithData {
  return "data" in section && section.data !== undefined;
}
